import React from 'react';
import PropTypes from 'prop-types';
import { CONTENT_ITEM, POSITIONS } from 'config/types/prop';
import Link from 'components/link';
import ListViewItem from 'components/contentListViewItem';
import withStyles from 'isomorphic-style-loader/withStyles';
import withThemes from 'components/hoc/withThemes';

import defaultTheme from './contentList.css';
import twoUp from './twoUp.css';
import threeUp from './threeUp.css';
import linear from './linear.css';

export const ContentList = ({
  items,
  interstitials,
  heading,
  moreLink,
  moreLabel,
  ListItemComponent,
  imagePosition,
  theme,
  onFocus,
}) => {
  // Merge provided interstitials into list of content items
  // so we can render them in a single loop
  const mergedList = items.reduce((acc, curr, idx) => {
    const validInterstitials = interstitials.reduce((
      interstitialAcc,
      { frequency, count, content },
    ) => {
      const currentCount = count || 1; // Default to displaying once
      const position = idx + 1;

      if (
        0 === position % frequency &&
        currentCount >= (position / frequency)
      ) {
        return interstitialAcc.concat({
          key: `${position}${currentCount}`,
          contentListItemType: 'interstitial',
          content,
        });
      }

      return interstitialAcc;
    }, []);

    return acc.concat({
      contentListItemType: 'contentItem',
      content: curr,
    }, validInterstitials);
  }, []);

  return (
    <div className={theme.wrapper}>
      {heading && <div className={theme.heading}><span>{heading}</span></div>}
      <div className={theme.content}>
        {mergedList.map(({ contentListItemType, content, key }) => {
          if ('interstitial' === contentListItemType) {
            return (
              <span className={theme.interstitial} key={key}>
                {content}
              </span>
            );
          }

          return (
            <div className={theme.item} key={content.id} onFocus={onFocus}>
              <ListItemComponent
                item={content}
                imagePosition={imagePosition}
              />
            </div>
          );
        })}
      </div>
      {moreLink && <div>
        <Link to={moreLink}>{moreLabel}</Link>
      </div>}
    </div>
  );
};

ContentList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape(CONTENT_ITEM),
  ).isRequired,
  heading: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  interstitials: PropTypes.arrayOf(
    PropTypes.shape({
      frequency: PropTypes.number.isRequired,
      count: PropTypes.number,
      content: PropTypes.node.isRequired,
    }),
  ),
  moreLink: PropTypes.string,
  moreLabel: PropTypes.string,
  ListItemComponent: PropTypes.elementType,
  imagePosition: POSITIONS,
  theme: PropTypes.object.isRequired,
  onFocus: PropTypes.func,
};

ContentList.defaultProps = {
  heading: false,
  interstitials: [],
  moreLink: '',
  moreLabel: '',
  ListItemComponent: ListViewItem,
  imagePosition: 'full',
  onFocus: () => {},
};

const wrapWithStyles = withStyles(defaultTheme, twoUp, threeUp, linear);
const wrapWithThemes = withThemes(
  'ContentList',
  { defaultTheme, twoUp, threeUp, linear },
);

export default wrapWithStyles(wrapWithThemes(ContentList));
